import React, { useState, useEffect } from 'react';
import { 
  getPartyListAPI, 
  getTokenListAPI, 
  getInternalAccountListAPI,
  getDollarAccountListAPI,
  Party, 
  Token, 
  InternalAccount,
  DollarAccount 
} from '../../../../services/adminApi';
import styles from './TransactionForm.module.css';

interface AddSwapTransactionFormProps {
  onSubmit: (transactionData: SwapTransactionFormData) => void;
  onCancel: () => void;
  initialData?: Partial<SwapTransactionFormData>;
}

export interface SwapTransactionFormData {
  date: string;
  swapType: 'INR to Token' | 'Token to INR' | 'Dollar to INR' | 'INR to Dollar' | 'Dollar to Token' | 'Token to Dollar';
  payIn: string;
  partyId: string;
  partyName: string;
  fromAccountId: string;
  fromAccount: string;
  fromDollarAccountId: string;
  fromDollarAccount: string;
  fromTokenId: string;
  fromToken: string;
  toAccountId: string;
  toAccount: string;
  toTokenId: string;
  toToken: string;
  toDollarAccountId: string;
  toDollarAccount: string;
  quantity: number;
  tokenRate: number;
  dollarRate: number;
  slippagePercent: number;
  baseAmount: number;
  slippageAmount: number;
  finalAmount: number;
  remarks?: string;
}

const AddSwapTransactionForm: React.FC<AddSwapTransactionFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
}) => {
  const [formData, setFormData] = useState<SwapTransactionFormData>({
    date: new Date().toISOString().split('T')[0],
    swapType: 'INR to Token',
    payIn: 'INR',
    partyId: '',
    partyName: '',
    fromAccountId: '',
    fromAccount: '',
    fromDollarAccountId: '',
    fromDollarAccount: '',
    fromTokenId: '',
    fromToken: '',
    toAccountId: '',
    toAccount: '',
    toTokenId: '',
    toToken: '',
    toDollarAccountId: '',
    toDollarAccount: '',
    quantity: 0,
    tokenRate: 0,
    dollarRate: 83,
    slippagePercent: 0,
    baseAmount: 0,
    slippageAmount: 0,
    finalAmount: 0,
    remarks: '',
  });

  const [parties, setParties] = useState<Party[]>([]);
  const [tokens, setTokens] = useState<Token[]>([]);
  const [accounts, setAccounts] = useState<InternalAccount[]>([]);
  const [dollarAccounts, setDollarAccounts] = useState<DollarAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Determine FROM and TO labels based on swap type
  const getFromToLabels = () => {
    switch (formData.swapType) {
      case 'INR to Token':
        return { from: 'FROM', to: 'TO', fromType: 'account', toType: 'token' };
      case 'Token to INR':
        return { from: 'FROM', to: 'TO', fromType: 'token', toType: 'account' };
      case 'Dollar to INR':
        return { from: 'FROM', to: 'TO', fromType: 'dollar', toType: 'account' };
      case 'INR to Dollar':
        return { from: 'FROM', to: 'TO', fromType: 'account', toType: 'dollar' };
      case 'Dollar to Token':
        return { from: 'FROM', to: 'TO', fromType: 'dollar', toType: 'token' };
      case 'Token to Dollar':
        return { from: 'FROM', to: 'TO', fromType: 'token', toType: 'dollar' };
      default:
        return { from: 'FROM', to: 'TO', fromType: 'account', toType: 'token' };
    }
  };

  const labels = getFromToLabels();

  // Load dropdown data
  useEffect(() => {
    const loadData = async () => {
      try {
        const [partiesResponse, tokensResponse, accountsResponse, dollarAccountsResponse] = await Promise.all([
          getPartyListAPI({ pageNo: 1, pageSize: 100 }),
          getTokenListAPI({ pageNo: 1, pageSize: 100 }),
          getInternalAccountListAPI({ pageNo: 1, pageSize: 100 }),
          getDollarAccountListAPI({ pageNo: 1, pageSize: 100 }),
        ]);
        setParties((partiesResponse.data || []).filter(p => p.isActive));
        setTokens((tokensResponse.data || []).filter(t => t.isActive));
        setAccounts((accountsResponse.data || []).filter(a => a.isActive));
        setDollarAccounts((dollarAccountsResponse.data || []).filter(da => da.isActive));
      } catch (error) {
        console.error('Failed to load form data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Populate form with initial data when editing
  useEffect(() => {
    if (initialData) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
      }));
    }
  }, [initialData]);

  // Calculate amounts when values change
  useEffect(() => {
    const baseAmount = formData.quantity * formData.tokenRate * formData.dollarRate;
    const slippageAmount = baseAmount * (formData.slippagePercent / 100);
    const finalAmount = baseAmount - slippageAmount;
    
    setFormData((prev) => ({
      ...prev,
      baseAmount,
      slippageAmount,
      finalAmount,
    }));
  }, [formData.quantity, formData.tokenRate, formData.dollarRate, formData.slippagePercent]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePartyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const partyId = e.target.value;
    const partyIdNum = parseInt(partyId);
    const party = parties.find(p => p.id === partyIdNum);
    setFormData((prev) => ({
      ...prev,
      partyId,
      partyName: party?.partyName || '',
    }));
  };

  const handleAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;
    const accountIdNum = parseInt(accountId);
    const account = accounts.find(a => a.id === accountIdNum);
    setFormData((prev) => ({
      ...prev,
      fromAccountId: accountId,
      fromAccount: account?.accountName || '',
    }));
  };

  const handleToAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;
    const accountIdNum = parseInt(accountId);
    const account = accounts.find(a => a.id === accountIdNum);
    setFormData((prev) => ({
      ...prev,
      toAccountId: accountId,
      toAccount: account?.accountName || '',
    }));
  };

  const handleTokenChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const tokenId = e.target.value;
    const tokenIdNum = parseInt(tokenId);
    const token = tokens.find(t => t.id === tokenIdNum);
    setFormData((prev) => ({
      ...prev,
      toTokenId: tokenId,
      toToken: token?.tokenName || '',
    }));
  };

  const handleFromDollarAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;
    const accountIdNum = parseInt(accountId);
    const account = dollarAccounts.find(a => a.id === accountIdNum);
    setFormData((prev) => ({
      ...prev,
      fromDollarAccountId: accountId,
      fromDollarAccount: account?.accountName || '',
    }));
  };

  const handleToDollarAccountChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accountId = e.target.value;
    const accountIdNum = parseInt(accountId);
    const account = dollarAccounts.find(a => a.id === accountIdNum);
    setFormData((prev) => ({
      ...prev,
      toDollarAccountId: accountId,
      toDollarAccount: account?.accountName || '',
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // if (!formData.fromAccountId && !formData.fromDollarAccountId) {
    //   alert('Please select FROM account');
    //   return;
    // }
    // if (!formData.toTokenId && !formData.toDollarAccountId) {
    //   alert('Please select TO account or token');
    //   return;
    // }
    setIsSubmitting(true);
    onSubmit(formData);
  };

  if (loading) {
    return <div className={styles.loading}>Loading form data...</div>;
  }

  return (
    <form className={`${styles.form} ${styles.swapForm}`} onSubmit={handleSubmit}>
      {/* Swap Type and Date Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="swapType" className={styles.label}>
            Swap Type <span className={styles.required}>*</span>
          </label>
          <select
            id="swapType"
            name="swapType"
            value={formData.swapType}
            onChange={handleChange}
            className={styles.select}
            required
          >
            <option value="INR to Token">INR to Token</option>
            <option value="Token to INR">Token to INR</option>
            <option value="Dollar to INR">Dollar to INR</option>
            <option value="INR to Dollar">INR to Dollar</option>
            <option value="Dollar to Token">Dollar to Token</option>
            <option value="Token to Dollar">Token to Dollar</option>
          </select>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="date" className={styles.label}>
            Date <span className={styles.required}>*</span>
          </label>
          <input
            id="date"
            type="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={styles.input}
            required
          />
        </div>
      </div>

      {/* Payment In and Party Row */}
      <div className={styles.row}>
        <div className={styles.formGroup}>
          <label htmlFor="payIn" className={styles.label}>
            Payment In <span className={styles.required}>*</span>
          </label>
          <select
            id="payIn"
            name="payIn"
            value={formData.payIn}
            onChange={handleChange}
            className={styles.select}
            required
          >
            <option value="INR">INR</option>
            <option value="USD">USD</option>
            <option value="Token">Token</option>
          </select>
        </div>
        <div className={styles.formGroup}>
          <label htmlFor="partyId" className={styles.label}>
            Party (Optional)
          </label>
          <div className={styles.inputWithButton}>
            <select
              id="partyId"
              name="partyId"
              value={formData.partyId}
              onChange={handlePartyChange}
              className={styles.select}
            >
              <option value="">-- None --</option>
              {parties.map((party) => (
                <option key={party.id} value={party.id}>
                  {party.partyName}
                </option>
              ))}
            </select>
            <button type="button" className={styles.addButton} title="Add new party">
              +
            </button>
          </div>
        </div>
      </div>

      {/* FROM and TO Sections Side by Side */}
      <div className={styles.row}>
        {/* FROM Section */}
        <div className={`${styles.section} ${styles.fromSection}`}>
          <h3 className={styles.sectionTitle}>{labels.from}</h3>
          <div className={styles.formGroup}>
            {labels.fromType === 'account' ? (
              <select
                id="fromAccountId"
                name="fromAccountId"
                value={formData.fromAccountId}
                onChange={handleAccountChange}
                className={styles.select}
                required
              >
                <option value="">Select INR Account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.accountName}
                  </option>
                ))}
              </select>
            ) : labels.fromType === 'dollar' ? (
              <select
                id="fromDollarAccountId"
                name="fromDollarAccountId"
                value={formData.fromDollarAccountId}
                onChange={handleFromDollarAccountChange}
                className={styles.select}
                required
              >
                <option value="">Select Dollar Account</option>
                {dollarAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.accountName}
                  </option>
                ))}
              </select>
            ) : (
              <select
                id="fromTokenId"
                name="toTokenId"
                value={formData.toTokenId}
                onChange={handleTokenChange}
                className={styles.select}
                required
              >
                <option value="">Select Token</option>
                {tokens.map((token) => (
                  <option key={token.id} value={token.id}>
                    {token.tokenName} ({token.symbol})
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>

        {/* TO Section */}
        <div className={`${styles.section} ${styles.toSection}`}>
          <h3 className={styles.sectionTitle}>{labels.to}</h3>
          <div className={styles.formGroup}>
            {labels.toType === 'token' ? (
              <select
                id="toTokenId"
                name="toTokenId"
                value={formData.toTokenId}
                onChange={handleTokenChange}
                className={styles.select}
                required
              >
                <option value="">Select Token</option>
                {tokens.map((token) => (
                  <option key={token.id} value={token.id}>
                    {token.tokenName} ({token.symbol})
                  </option>
                ))}
              </select>
            ) : labels.toType === 'dollar' ? (
              <select
                id="toDollarAccountId"
                name="toDollarAccountId"
                value={formData.toDollarAccountId}
                onChange={handleToDollarAccountChange}
                className={styles.select}
                required
              >
                <option value="">Select Dollar Account</option>
                {dollarAccounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.accountName}
                  </option>
                ))}
              </select>
            ) : (
              <select
                id="toAccountId"
                name="toAccountId"
                value={formData.toAccountId}
                onChange={handleToAccountChange}
                className={styles.select}
                required
              >
                <option value="">Select INR Account</option>
                {accounts.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.accountName}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Amount Calculation Section */}
      <div className={`${styles.section} ${styles.calculationSection}`}>
        <h3 className={styles.sectionTitle}>AMOUNT CALCULATION</h3>
        
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label htmlFor="quantity" className={styles.label}>
              Quantity <span className={styles.required}>*</span>
            </label>
            <input
              id="quantity"
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              className={styles.input}
              step="0.00000001"
              min="0"
              placeholder="0"
              required
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="tokenRate" className={styles.label}>
              Token to $ Rate
            </label>
            <input
              id="tokenRate"
              type="number"
              name="tokenRate"
              value={formData.tokenRate}
              onChange={handleChange}
              className={styles.input}
              step="0.00000001"
              min="0"
              placeholder="0"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="dollarRate" className={styles.label}>
              $ to INR Rate
            </label>
            <input
              id="dollarRate"
              type="number"
              name="dollarRate"
              value={formData.dollarRate}
              onChange={handleChange}
              className={styles.input}
              step="0.00000001"
              min="0"
              placeholder="0"
            />
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="slippagePercent" className={styles.label}>
              Slippage %
            </label>
            <input
              id="slippagePercent"
              type="number"
              name="slippagePercent"
              value={formData.slippagePercent}
              onChange={handleChange}
              className={styles.input}
              step="0.01"
              min="0"
              placeholder="0"
            />
          </div>
        </div>

        {/* Calculation Summary Box */}
        <div className={styles.calculationSummary}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Base Amount:</span>
            <span className={styles.summaryValue}>
              ₹{formData.baseAmount.toFixed(2)}
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Slippage:</span>
            <span className={`${styles.summaryValue} ${styles.slippageValue}`}>
              -₹{formData.slippageAmount.toFixed(2)}
            </span>
          </div>
          <div className={`${styles.summaryRow} ${styles.finalRow}`}>
            <span className={styles.summaryLabel}>Final Amount:</span>
            <span className={styles.summaryValue}>
              ₹{formData.finalAmount.toFixed(2)}
            </span>
          </div>
        </div>
      </div>

      {/* Remarks Section */}
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Remarks</h3>
        <div className={styles.formGroup}>
          <textarea
            id="remarks"
            name="remarks"
            value={formData.remarks}
            onChange={handleChange}
            className={styles.textarea}
            rows={3}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className={styles.actions}>
        <button type="button" onClick={onCancel} className={styles.cancelButton} disabled={isSubmitting}>
          Cancel
        </button>
        <button type="submit" className={styles.saveSwapButton} disabled={isSubmitting}>
          {isSubmitting ? 'Saving...' : 'Save Swap'}
        </button>
      </div>
    </form>
  );
};

export default AddSwapTransactionForm;
